import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, CircularProgress, Modal } from '@material-ui/core';
import clsx from 'clsx';
import { withAllowedOperationsProvider } from '../../hocs';
import { DISPLAY_MESSAGES, RESOURCE_TYPE } from '../../constants';
import defaultStyles from './AssignShiftPopup.module.css';
import jlrStyles from './AssignShiftPopupJlr.module.css';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import { buildErrorMessage } from '../../apis/calls';
import moment from 'moment';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import { setStylesAccordingToPlant } from '../../utils/utils';

const AssignShiftPopup = ({
  open,
  closeForm,
  shifts,
  refreshData,
  bulkAssignShift,
  dateOfAsns,
  dateOfAgsns,
  asns,
  totalQuantities,
}) => {
  const { showError, showSuccess } = usePopupManager();
  const [doesAssignmentExceedTotal, setDoesAssignmentExceedTotal] = useState(
    false,
  );
  const [
    doesAssignmentFallBehindTotal,
    setDoesAssignmentFallBehindTotal,
  ] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [currentAssignmentsTotal, setCurrentAssignmentsTotal] = useState(null);

  const [
    assignmentsToShiftLabelsMap,
    setAssignmentsToShiftLabelsMap,
  ] = useState({});

  const [styles, setStyles] = useState(defaultStyles);
  const [plant] = usePlantAndLinesStore().plantState;

  useEffect(() => {
    setAssignmentsToShiftLabelsMap(
      shifts
        .filter(shift => shift.isAvailable)
        .map(shift => shift.label)
        .reduce((acc, currShift) => ({ ...acc, [currShift]: '' }), {}),
    );
    return () => setAssignmentsToShiftLabelsMap({});
  }, [shifts, open]);

  useEffect(() => {
    setStylesAccordingToPlant(plant, setStyles, defaultStyles, jlrStyles);
  }, [plant]);

  const getSumOfAssignments = assignmentsMap =>
    Object.values(assignmentsMap)
      .filter(assignment => assignment)
      .reduce((acc, curr) => acc + curr, 0);

  useEffect(() => {
    const sumOfCurrentAssignments = getSumOfAssignments(
      assignmentsToShiftLabelsMap,
    );
    setCurrentAssignmentsTotal(sumOfCurrentAssignments);
    setDoesAssignmentExceedTotal(sumOfCurrentAssignments > totalQuantities);
    setDoesAssignmentFallBehindTotal(sumOfCurrentAssignments < totalQuantities);
  }, [totalQuantities, assignmentsToShiftLabelsMap]);

  const handleCancel = () => closeForm();

  const handleApply = async () => {
    if (
      doesAssignmentExceedTotal ||
      doesAssignmentFallBehindTotal ||
      !totalQuantities
    )
      return;

    const sumOfCurrentAssignments = getSumOfAssignments(
      assignmentsToShiftLabelsMap,
    );

    if (sumOfCurrentAssignments === 0) return;

    try {
      setIsApplying(true);

      const sortedAsns = asns.sort((a, b) => a.serialNumber - b.serialNumber);
      const requiredAvailableAsns = sortedAsns.slice(
        0,
        sumOfCurrentAssignments,
      );

      const sortedRequiredShifts = shifts
        .filter(shift => shift.isAvailable)
        .sort((a, b) => {
          if (a.label < b.label) {
            return -1;
          }
          if (a.label > b.label) {
            return 1;
          }
          return 0;
        });

      await bulkAssignShift(
        sortedRequiredShifts,
        requiredAvailableAsns,
        assignmentsToShiftLabelsMap,
      );

      closeForm();
      showSuccess({
        contextText: DISPLAY_MESSAGES.SHIFT_ASSIGNMENT_SUCCESS,
      });
      refreshData();
    } catch (error) {
      showError({ contextText: 'Failed to assign shifts', error });
      console.error(
        'failed to bulk assign shifts, error:' + buildErrorMessage(error),
      );
    } finally {
      setIsApplying(false);
    }
  };

  const isWholeNumber = val => /^[0-9]*$/.test(val);

  const handleShiftAssignment = shift => e => {
    const { value } = e.target;
    if (!isWholeNumber(value)) {
      window.alert('Pls enter whole numbers only.');
      return;
    }
    setAssignmentsToShiftLabelsMap(prev => ({
      ...prev,
      [shift.label]: Number(value),
    }));
  };

  const renderShiftRow = shift => {
    if (shift.isAvailable) {
      return (
        <div key={shift.label} className={styles.row}>
          <div className={styles.label}>{shift.label}</div>
          <div className={styles.description}>
            <input
              data-testid={`input-for-shift-${shift.label}`}
              value={assignmentsToShiftLabelsMap[shift.label]}
              type="text"
              placeholder="000"
              className={clsx(
                styles.textfield,
                (doesAssignmentExceedTotal || doesAssignmentFallBehindTotal) &&
                  styles.errorTextfield,
              )}
              onChange={handleShiftAssignment(shift)}
            ></input>
          </div>
        </div>
      );
    }
    return (
      <div key={shift.label} className={styles.shiftRow}>
        <div style={{ fontSize: 16 }} className={styles.label}>
          {shift.label} Shift
        </div>
        <div className={styles.description}>- Not Planned -</div>
      </div>
    );
  };

  return (
    <Modal open={open} onClose={closeForm}>
      <div className={styles.formContainer} data-testid="asssign-shift-modal">
        <div className={styles.formTitle}>
          <span>Shift Assignment</span>
        </div>
        <div className={styles.note}>
          <span>Note: Assignment will be made in sequence</span>
        </div>
        <div>
          <div className={styles.dateAndQuantityContainer}>
            <label className={styles.inputLabel}>
              Date
              <input
                data-testid="date"
                type="text"
                value={dateOfAgsns || dateOfAsns?.format('DD/MM/yyyy')}
                disabled
                style={{ cursor: 'not-allowed', marginLeft: '115px' }}
                className={clsx(styles.textfield, styles.disabledTextField)}
              />
            </label>
            <label className={styles.inputLabel}>
              Quantity
              <input
                data-testid="quantity"
                type="text"
                value={totalQuantities}
                disabled
                style={{ cursor: 'not-allowed', marginLeft: '92px' }}
                className={clsx(styles.textfield, styles.disabledTextField)}
              />
            </label>
          </div>
          <hr
            style={{
              color: '#CDD4E0',
              backgroundColor: '#CDD4E0',
              height: 1,
              border: 'none',
            }}
          />
          <div className={styles.shiftAssignedRow}>
            <div className={styles.shiftHeader}>Shift</div>
            <div className={styles.assignedHeader}>Assigned</div>
          </div>
          <div className={styles.errorRow}>
            {doesAssignmentExceedTotal && (
              <span className={styles.error}>{`"Assigned count exceed by ${
                currentAssignmentsTotal - totalQuantities
              } Available quantity"`}</span>
            )}
            {doesAssignmentFallBehindTotal && (
              <span className={styles.error}>{`"Assigned count is short by ${
                totalQuantities - currentAssignmentsTotal
              } Available quantity"`}</span>
            )}
          </div>
          {shifts.map(shift => renderShiftRow(shift))}
          <div className={styles.bottomRow}>
            <Button
              className={clsx(styles.button, styles.cancleButton)}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              className={clsx(styles.button, styles.confirmButton)}
              data-testid="bulk-assign-shift-btn"
              onClick={handleApply}
              disabled={currentAssignmentsTotal !== totalQuantities}
            >
              {isApplying ? (
                <CircularProgress size="1.3rem" style={{ color: 'white' }} />
              ) : (
                'Confirm'
              )}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

AssignShiftPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  closeForm: PropTypes.func.isRequired,
  shifts: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
      isAvailable: PropTypes.bool.isRequired,
    }),
  ).isRequired,
  refreshData: PropTypes.func.isRequired,
  bulkAssignShift: PropTypes.func.isRequired,
  dateOfAsns: PropTypes.objectOf(moment).isRequired,
  dateOfAgsns: PropTypes.objectOf(moment).isRequired,
  totalQuantities: PropTypes.number.isRequired,
  asns: PropTypes.arrayOf(
    PropTypes.shape({
      asnId: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      serialNumber: PropTypes.number.isRequired,
    }),
  ),
};

export { AssignShiftPopup as PureAssignShiftPopup };

export default withAllowedOperationsProvider(
  AssignShiftPopup,
  false,
  RESOURCE_TYPE.CHASSIS,
);
